import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPage } from '@/pages/Map'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { CommandPalette } from '@/components/CommandPalette'
import { DomainsAPI } from '@/api/domains'
import { Onboarding } from '@/components/Onboarding'

// public site pages ride lazy chunks — marketing weight stays off the app bundle
const SiteHow = lazy(() => import('@/pages/site/SiteHow').then((m) => ({ default: m.SiteHow })))
const SiteDomains = lazy(() => import('@/pages/site/SiteDomains').then((m) => ({ default: m.SiteDomains })))
const SiteSprings = lazy(() => import('@/pages/site/SiteSprings').then((m) => ({ default: m.SiteSprings })))
const Roadmap = lazy(() => import('@/pages/site/Roadmap').then((m) => ({ default: m.Roadmap })))
const Changelog = lazy(() => import('@/pages/site/Changelog').then((m) => ({ default: m.Changelog })))
const Terms = lazy(() => import('@/pages/site/Terms').then((m) => ({ default: m.Terms })))
const Privacy = lazy(() => import('@/pages/site/Privacy').then((m) => ({ default: m.Privacy })))

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>{children}</Suspense>
}
import { Layout } from '@/components/Layout'
import { useUI } from '@/store/ui'
import { Dashboard } from '@/pages/Dashboard'
import { Domains } from '@/pages/Domains'
import { DomainPage } from '@/pages/DomainPage'
import { PublicRegions } from '@/pages/PublicRegions'
import { Explore } from '@/pages/Explore'
import { Invite } from '@/pages/Invite'
import { GraphView } from '@/pages/GraphView'
import { Landing } from '@/pages/Landing'
import { Login } from '@/pages/Login'
import { Waitlist } from '@/pages/Waitlist'
import { useRegistrationStatus } from '@/lib/registrationStatus'
import { GateCallback } from '@/pages/GateCallback'
import { Agents } from '@/pages/Agents'
import { Commune } from '@/pages/Commune'
import { Missions } from '@/pages/Missions'
import { MissionPage } from '@/pages/MissionPage'
import { People } from '@/pages/People'
import { Pricing } from '@/pages/Pricing'
import { Signals } from '@/pages/Signals'
import { NeedsYouPage } from '@/pages/NeedsYouPage'
import { Sigils } from '@/pages/Sigils'
import { FamiliarPage } from '@/pages/FamiliarPage'
import { Trove } from '@/pages/Trove'
import { Pipelines } from '@/pages/Pipelines'
import { Ledger } from '@/pages/Ledger'
import { Briefing } from '@/pages/Briefing'
import { PersonDetail } from '@/pages/PersonDetail'
import { Placeholder } from '@/pages/Placeholder'
import { Profile } from '@/pages/Profile'
import { Settings } from '@/pages/Settings'

// The app is for the signed-in; everyone else meets the marketing sky at /.
// A logged-out visitor is bounced to /login, but we carry WHERE they were headed
// (as router state) so the gate can return them there — most crucially an
// /invite/:token deep-link, which must never be discarded.
function RequireAuth() {
  const token = useUI((s) => s.token)
  const location = useLocation()
  if (token) return <Outlet />
  const from = `${location.pathname}${location.search}`
  return <Navigate to="/login" replace state={{ from }} />
}

// First-run gate: a signed-in keeper with ZERO owned domains has never shaped their world, so
// we route them into the Tala-guided onboarding instead of dropping them into an empty app. It
// wraps only the Layout route group — /onboarding itself sits outside the gate so it can't loop.
// Guards against trapping anyone: it fires only once the domains list has actually loaded (never
// on an error/slow fetch), respects the persisted "skip" flag, and any created domain leaves
// first-run naturally. Shared-only members (domains they've merely joined) still count as empty,
// so they too get the guided path the first time.
function FirstRunGate() {
  const { diwaUrl, onboardingDismissed } = useUI()
  const location = useLocation()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const { data, isPending, isError } = useQuery({ queryKey: ['domains'], queryFn: () => api.list() })

  // An invite deep-link must ALWAYS redeem — never intercept /invite/:token, or a brand-new
  // (still-empty) collaborator would be bounced into onboarding and lose the invite.
  if (location.pathname.startsWith('/invite/')) return <Outlet />
  if (isPending) return <div style={{ minHeight: '100vh' }} />
  if (isError) return <Outlet /> // never trap on a failed fetch — let the app render
  // "Empty void" = no domains of ANY kind. A keeper who's joined a shared region already has a
  // world to look at, so they skip onboarding (and this is what invite redeemers land on).
  const hasAnyDomain = (data?.domains ?? []).length > 0
  if (!hasAnyDomain && !onboardingDismissed) return <Navigate to="/onboarding" replace />
  return <Outlet />
}

// The signup door: /signup and /register are real addresses people type and
// links carry. Where they lead is config (DIWA_REGISTRATION_GATED, relayed on
// /auth/gate/status): gated → the waitlist story; open → straight to signup.
// Until the probe answers we hold a blank frame rather than flash the wrong door.
function SignupDoor() {
  const open = useRegistrationStatus()
  if (open === undefined) return <div style={{ minHeight: '100vh' }} />
  return <Navigate to={open ? '/login?mode=signup' : '/waitlist'} replace />
}

// The command palette (Cmd/Ctrl-K) is a signed-in affordance: it navigates the
// app's authenticated routes, so it only arms once there's a token. Mounting it
// here — at the app root, above the Routes — keeps it available on every page
// and out of the marketing/login surfaces owned elsewhere.
function CommandPaletteHost() {
  const token = useUI((s) => s.token)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!token) {
      setOpen(false)
      return
    }
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [token])

  if (!token) return null
  return <CommandPalette open={open} onClose={() => setOpen(false)} />
}

export default function App() {
  return (
    <>
      <CommandPaletteHost />
      <Routes>
      {/* diwa.domains leads with the marketing page; the app lives behind /home. */}
      <Route index element={<Landing />} />
      <Route path="/login" element={<Login />} />
      {/* The Threshold: waitlist + early-access codes, standing on its own address. */}
      <Route path="/waitlist" element={<Waitlist />} />
      {/* Register-shaped addresses obey the same one story — see SignupDoor. */}
      <Route path="/signup" element={<SignupDoor />} />
      <Route path="/register" element={<SignupDoor />} />
      {/* Registered Gate callback — one-login handoff (one_login_spec.md §4). */}
      <Route path="/auth/callback" element={<GateCallback />} />
      <Route path="/site/how" element={<Lazy><SiteHow /></Lazy>} />
      <Route path="/site/domains" element={<Lazy><SiteDomains /></Lazy>} />
      <Route path="/site/springs" element={<Lazy><SiteSprings /></Lazy>} />
      <Route path="/r" element={<PublicRegions />} />
      {/* Public, no-auth demo world — a live read-only example a visitor can wander. */}
      <Route path="/demo" element={<Explore />} />
      <Route path="/roadmap" element={<Lazy><Roadmap /></Lazy>} />
      <Route path="/changelog" element={<Lazy><Changelog /></Lazy>} />
      <Route path="/terms" element={<Lazy><Terms /></Lazy>} />
      <Route path="/privacy" element={<Lazy><Privacy /></Lazy>} />
      <Route path="/domicile" element={<Navigate to="/" replace />} />
      <Route element={<RequireAuth />}>
        {/* Full-screen guided onboarding — outside the Layout AND the first-run gate. */}
        <Route path="onboarding" element={<Onboarding />} />
        <Route element={<FirstRunGate />}>
        <Route element={<Layout />}>
          <Route path="home" element={<Dashboard />} />
          <Route path="map" element={<MapPage />} />
          <Route path="people" element={<People />} />
          <Route path="people/:id" element={<PersonDetail />} />
          <Route path="graph" element={<GraphView />} />
          <Route path="domains" element={<Domains />} />
          {/* One region's own page — the domain as a place (cards link through here). */}
          <Route path="domains/:slug" element={<DomainPage />} />
          <Route path="signals" element={<Signals />} />
          <Route path="needs" element={<NeedsYouPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="sigils" element={<Sigils />} />
          <Route path="familiar" element={<FamiliarPage />} />
          <Route path="missions" element={<Missions />} />
          {/* One mission's own page — the machine's work as a place (cards link through here). */}
          <Route path="missions/:id" element={<MissionPage />} />
          <Route path="agents" element={<Agents />} />
          {/* Commune — Diwa's community region: the town square, with a doorway into Eden. */}
          <Route path="commune" element={<Commune />} />
          <Route path="trove" element={<Trove />} />
          <Route path="pipelines" element={<Pipelines />} />
          <Route path="ledger" element={<Ledger />} />
          <Route path="briefing" element={<Briefing />} />
          {/* Vault → Trove: "Vault" now means only the bronze artifact store. */}
          <Route path="vault" element={<Navigate to="/trove" replace />} />
          <Route path="invite/:token" element={<Invite />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="companies" element={<Placeholder title="Companies" note="Company graph view — wires to GET /companies." />} />
          <Route path="pipeline" element={<Placeholder title="Pipeline" note="Relationship pipeline — wires to GET /pipeline." />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
